import React, { useMemo, useState } from 'react';
import { QrCode, ChevronUp } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const MEMBER_ID = 'IHSAN-2026-0042';
const GRID = 25;

// A real QR needs an encoder library; this prototype has no live member record to
// encode, so the code is drawn deterministically from the member id. It looks and
// frames like a QR for demo purposes but is not scannable — swap in a proper
// encoder once member ids are real.
function buildMatrix(seedText) {
  let h = 2166136261;
  for (let i = 0; i < seedText.length; i += 1) {
    h ^= seedText.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const next = () => {
    h ^= h << 13; h ^= h >>> 17; h ^= h << 5;
    return (h >>> 0) / 4294967296;
  };

  const isFinder = (r, c) =>
    (r < 7 && c < 7) || (r < 7 && c >= GRID - 7) || (r >= GRID - 7 && c < 7);

  const cells = [];
  for (let r = 0; r < GRID; r += 1) {
    for (let c = 0; c < GRID; c += 1) {
      if (isFinder(r, c)) continue;
      if (next() > 0.52) cells.push([r, c]);
    }
  }
  return cells;
}

const FinderPattern = ({ x, y }) => (
  <g transform={`translate(${x} ${y})`}>
    <rect width="7" height="7" fill="#0F172A" rx="1.4" />
    <rect x="1" y="1" width="5" height="5" fill="#fff" rx="0.8" />
    <rect x="2" y="2" width="3" height="3" fill="#0F172A" rx="0.5" />
  </g>
);

export default function BenefitsQrCard() {
  const [open, setOpen] = useState(false);
  const cells = useMemo(() => buildMatrix(MEMBER_ID), []);

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        data-testid="benefits-qr-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left"
      >
        <CardContent className="p-5 flex items-center gap-4">
          <div className="w-20 h-20 rounded-lg border-2 border-dashed border-border bg-muted/40 flex items-center justify-center flex-shrink-0">
            <QrCode className="h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Scan to access your benefits</p>
            <p className="text-xs text-muted-foreground mt-1">No login needed at service points</p>
            <p className="text-xs font-semibold text-primary mt-1.5">
              {open ? 'Hide QR code' : 'Tap to show QR code'}
            </p>
          </div>
          <ChevronUp
            className={`h-4 w-4 text-muted-foreground transition-transform duration-300 ${open ? '' : 'rotate-180'}`}
          />
        </CardContent>
      </button>

      {/* Grid-rows trick animates to the content's natural height without a fixed max. */}
      <div
        className={`grid transition-all duration-300 ease-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-5 pb-6 flex flex-col items-center" data-testid="benefits-qr-panel">
            <div className="rounded-2xl bg-white p-3.5 shadow-md border border-border/60">
              <svg
                viewBox={`-1 -1 ${GRID + 2} ${GRID + 2}`}
                className="w-40 h-40 md:w-48 md:h-48"
                role="img"
                aria-label={`Member QR code for ${MEMBER_ID}`}
              >
                <rect x="-1" y="-1" width={GRID + 2} height={GRID + 2} fill="#fff" />
                {cells.map(([r, c]) => (
                  <rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" fill="#0F172A" />
                ))}
                <FinderPattern x={0} y={0} />
                <FinderPattern x={GRID - 7} y={0} />
                <FinderPattern x={0} y={GRID - 7} />
              </svg>
            </div>
            <p className="mt-3 text-sm font-bold tracking-wide text-[#0F172A]">{MEMBER_ID}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Show this at any MTFA service point
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
